import { createContext, useContext } from 'react';
import type { User } from '@/shared/types';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export type UserContextValue = {
  user: User | null;
  status: AuthStatus;
  // True when the session ended because a token refresh failed (vs. a deliberate
  // logout or a never-authenticated visitor). Lets /login explain what happened.
  sessionExpired: boolean;
  // Kicks off the Google flow by redirecting the browser to the consent screen.
  loginWithGoogle: () => Promise<void>;
  // Called by the /auth/callback route with the access token from the URL fragment.
  completeGoogleLogin: (accessToken: string) => Promise<void>;
  logout: () => Promise<void>;
  // DEV-only escape hatch for working offline without the backend auth endpoints.
  mockLogin?: (metadata: { displayName: string; email: string }) => void;
};

export const UserContext = createContext<UserContextValue | undefined>(undefined);

export function useCurrentUser() {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error('useCurrentUser must be used within a UserProvider');
  }
  return ctx;
}
